import { Dayjs } from 'dayjs';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { View, Views } from 'react-big-calendar';

import dateService from '@gem/services/date-service';

import { ConfirmationType, IParticipant } from 'APP/model/calendar/calendar.types';

interface ICalendarEventPresenterData {
  start: Dayjs | null;
  end: Dayjs | null;
  isTemplate: boolean;
  currentView: string;
  allDay: boolean;
  participants: IParticipant[];
  providerEmail: string;
}

interface ICalendarEventPresenter {
  data: ICalendarEventPresenterData;
  differenceInMinutes: number;
  differenceInHourse: number;
  wrapClass: string;
  containerClass: string;
  startTime: string;
  endTime: string;
  isWrapContent: boolean;
  isTimeAfterContent: boolean;
  isTimeBeforContent: boolean;
  setData(data: ICalendarEventPresenterData): void;
  backgraundClass: string;
  mapEventBackgraundClasses: { [key in ConfirmationType]: string };
}

interface ICalendarEventPresenterParams {
  start: Dayjs;
  end: Dayjs;
  currentView: View;
  isTemplate: boolean;
  allDay: boolean;
  providerEmail: string;
  getMyEventStatus(event: IParticipant[], providerEmail: string): ConfirmationType;
  participants: IParticipant[];
}

export const useCalendarEventPresenter = (
  params: ICalendarEventPresenterParams
): ICalendarEventPresenter => {
  const {
    start,
    end,
    isTemplate,
    currentView,
    allDay,
    getMyEventStatus,
    participants,
    providerEmail,
  } = params;

  const presenter = useLocalObservable<ICalendarEventPresenter>(() => ({
    data: {
      start: null,
      end: null,
      isTemplate: false,
      currentView: '',
      allDay: false,
      participants: [],
      providerEmail: '',
      isOvernight: false,
    },
    mapEventBackgraundClasses: {
      [ConfirmationType.NO_ANSWER]: 'stroke',
      [ConfirmationType.ACCEPTED]: 'filled',
      [ConfirmationType.DECLINED]: 'stroke strike',
      [ConfirmationType.MAYBE]: 'sharding',
    },

    setData(data: ICalendarEventPresenterData): void {
      presenter.data = data;
    },

    get differenceInMinutes(): number {
      return presenter.data.end?.diff(presenter.data.start, 'minute') || 0;
    },

    get differenceInHourse(): number {
      return presenter.data.end?.diff(presenter.data.start, 'hour') || 0;
    },

    get wrapClass(): string {
      return presenter.isWrapContent ? 'event-container-content-wrap' : 'event-container-content';
    },

    get backgraundClass(): string {
      const myStatus = getMyEventStatus(presenter.data.participants, presenter.data.providerEmail);

      return (
        presenter.mapEventBackgraundClasses[myStatus] ||
        presenter.mapEventBackgraundClasses[ConfirmationType.NO_ANSWER]
      );
    },

    get containerClass(): string {
      return presenter.data.isTemplate
        ? 'event-container-template'
        : `purple ${presenter.backgraundClass}`;
    },

    get isWrapContent(): boolean {
      return (
        (presenter.differenceInMinutes > 15 || !!presenter.differenceInHourse) &&
        presenter.data.currentView !== Views.MONTH
      );
    },

    get isTimeAfterContent(): boolean {
      return !presenter.data.allDay && presenter.data.currentView !== Views.MONTH;
    },

    get isTimeBeforContent(): boolean {
      return !presenter.data.allDay && presenter.data.currentView === Views.MONTH;
    },

    get startTime(): string {
      return (
        dateService.format(presenter.data.start?.toDate(), {
          hour: 'numeric',
          minute: 'numeric',
        }) || ''
      );
    },

    get endTime(): string {
      return (
        dateService.format(presenter.data.end?.toDate(), {
          hour: 'numeric',
          minute: 'numeric',
        }) || ''
      );
    },
  }));

  useEffect(() => {
    presenter.setData({
      start,
      end,
      isTemplate,
      currentView,
      allDay,
      participants,
      providerEmail,
    });
  }, [start, end, isTemplate, currentView, allDay, participants, providerEmail]);

  return presenter;
};
